import { useFormik } from 'formik'
import React, { ChangeEvent, useEffect, useState } from 'react'
import ModalComponent from '..'
import { useModalControl } from '../../../hooks/useModalControl'
import { HomepageBannerContent } from '../../../redux-toolkits/home-page/homePage.type'
import { AddHomeBannerSchema } from '../../../validation/validate'
import { Button } from '../../button'
import { ImageUploadButton } from '../../button/imageUploadButton'
import { ImageBanner } from '../../images/imageBanner'
import { Checkbox } from '../../inputs/Checkbox'
import { EditInput } from '../../inputs/editInput'
import { ImageUploadPreviewModal } from './ImagePreviewModal'

export const AddHomeBannerForm = ({
  onSuccessCallback = () => {},
  content,
  open,
  mode,
  loading,
  proceedText = 'Continue',
}: {
  onSuccessCallback?: (val: Omit<HomepageBannerContent, '_id'>) => void
  content?: HomepageBannerContent
  open: boolean
  mode: 'edit' | 'create'
  loading?: boolean
  proceedText?: string
}) => {
  const [uploadedImage, setUploadedImage] = useState<File>()
  const [uploadedMobileBanner, setUploadedMobileBanner] = useState<File>()

  const [editIndex, setEditIndex] = useState<number>()

  const { modal, modalOpen, setModalOpen, handleModalOpen } = useModalControl()

  const handleFileChange = (
    event: ChangeEvent<HTMLInputElement>,
    type: string,
  ) => {
    if (!event.target.files) {
      return
    }

    const selectedFile = event.target.files[0]

    type === 'desktop'
      ? setUploadedImage(selectedFile)
      : setUploadedMobileBanner(selectedFile)

    handleModalOpen('imageUploadPreviewModal')
  }

  const removeEmptyFields = (obj: any): any => {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(
          ([_, value]) => value !== '' && value !== null && value !== undefined,
        )
        .map(([key, value]) => [
          key,
          typeof value === 'object' && !Array.isArray(value)
            ? removeEmptyFields(value)
            : value,
        ]),
    )
  }

  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    touched,
    setFieldValue,
    isValid,
    resetForm,
  } = useFormik({
    initialValues: {
      desktopImage: content?.desktopImage ?? '',
      mobileImage: content?.mobileImage ?? '',
      heading: content?.heading ?? '',
      subtext: content?.subtext ?? '',
      buttonText: content?.buttonText ?? '',
      buttonUrl: content?.buttonUrl ?? '',
      secondaryButtonText: content?.secondaryButtonText ?? '',
      secondaryButtonUrl: content?.secondaryButtonUrl ?? '',
      isBackgroundDark: content?.isBackgroundDark ?? false,
    },
    enableReinitialize: true,
    validationSchema: AddHomeBannerSchema,
    onSubmit: (values) => {
      const data = removeEmptyFields(values)
      onSuccessCallback(data as any)
    },
  })

  useEffect(() => {
    if (!open) {
      resetForm()
    }
  }, [open])

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          <div className="mb-3">
            <h1>Desktop Image</h1>
          </div>
          <>
            {values.desktopImage ? (
              <ImageBanner
                imageUrl={values.desktopImage}
                loading={false}
                className="rounded-md max-h-[192px] bg-cover"
                errors={errors.desktopImage ? errors.desktopImage : ''}
                onImageEdit={(e) => {
                  handleFileChange(e, 'desktop')
                }}
                editIconPos="top-right"
              />
            ) : (
              <ImageUploadButton
                handleChange={(e) => {
                  handleFileChange(e, 'desktop')
                }}
                showText
                icon={'pic-icon'}
                iconDim={52}
                text={`Upload image (The maximum size supported is 4.77MB)`}
                className="
              !relative !w-full
              !m-0 !top-0 !right-0
              !h-fit
              !rounded-lg
              !col-span-full
              "
              />
            )}
            {errors.desktopImage ? (
              <div className="text-danger">{errors.desktopImage}</div>
            ) : (
              ''
            )}
          </>

          <div className="mb-3">
            <h1>Mobile Image</h1>
          </div>
          <>
            {values.mobileImage ? (
              <ImageBanner
                imageUrl={values.mobileImage}
                loading={false}
                className="rounded-md max-h-[192px] bg-cover"
                errors={errors.mobileImage ? errors.mobileImage : ''}
                onImageEdit={(e) => {
                  handleFileChange(e, 'mobile')
                }}
                editIconPos="top-right"
              />
            ) : (
              <ImageUploadButton
                handleChange={(e) => {
                  handleFileChange(e, 'mobile')
                }}
                showText
                icon={'pic-icon'}
                iconDim={52}
                text={`Upload image (The maximum size supported is 4.77MB)`}
                className="
              !relative !w-full
              !m-0 !top-0 !right-0
              !h-fit
              !rounded-lg
              !col-span-full
              "
              />
            )}
            {errors.mobileImage ? (
              <div className="text-danger">{errors.mobileImage}</div>
            ) : (
              ''
            )}
          </>

          <EditInput
            title="Heading"
            value={values.heading}
            onChange={handleChange}
            type="textarea"
            placeholder="Heading"
            onEditSave={() => {
              setEditIndex(undefined)
            }}
            editing={editIndex === 1}
            onEditClick={() => {
              setEditIndex(1)
            }}
            onCancel={() => {
              if (mode === 'create') {
                setFieldValue('heading', '')
              } else {
                setFieldValue('heading', content?.heading)
              }
              setEditIndex(undefined)
            }}
            loading={false}
            inputName="heading"
            mode={mode === 'create' ? 'edit' : 'edit'}
            errors={touched.heading && errors.heading ? errors.heading : ''}
          />

          <EditInput
            title="Subtext"
            value={values.subtext}
            onChange={handleChange}
            type="textarea"
            placeholder="Subtext"
            onEditSave={() => {
              setEditIndex(undefined)
            }}
            editing={editIndex === 3}
            onEditClick={() => {
              setEditIndex(3)
            }}
            onCancel={() => {
              if (mode === 'create') {
                setFieldValue('subtext', '')
              } else {
                setFieldValue('subtext', content?.subtext)
              }
              setEditIndex(undefined)
            }}
            loading={false}
            inputName="subtext"
            mode={mode === 'create' ? 'edit' : 'edit'}
            errors={touched.subtext && errors.subtext ? errors.subtext : ''}
          />

          <EditInput
            title="Button Text"
            value={values.buttonText}
            onChange={handleChange}
            type="text"
            placeholder="Button Text"
            onEditSave={() => {
              setEditIndex(undefined)
            }}
            editing={editIndex === 2}
            onEditClick={() => {
              setEditIndex(2)
            }}
            onCancel={() => {
              if (mode === 'create') {
                setFieldValue('buttonText', '')
              } else {
                setFieldValue('buttonText', content?.buttonText)
              }
              setEditIndex(undefined)
            }}
            loading={false}
            inputName="buttonText"
            mode={mode === 'create' ? 'edit' : 'edit'}
            errors={
              touched.buttonText && errors.buttonText ? errors.buttonText : ''
            }
          />

          <EditInput
            title="Button URL"
            value={values.buttonUrl}
            onChange={handleChange}
            type="text"
            placeholder="URL"
            onEditSave={() => {
              setEditIndex(undefined)
            }}
            editing={editIndex === 4}
            onEditClick={() => {
              setEditIndex(4)
            }}
            onCancel={() => {
              if (mode === 'create') {
                setFieldValue('buttonUrl', '')
              } else {
                setFieldValue('buttonUrl', content?.buttonUrl)
              }
              setEditIndex(undefined)
            }}
            loading={false}
            inputName="buttonUrl"
            mode={mode === 'create' ? 'edit' : 'edit'}
            errors={
              touched.buttonUrl && errors.buttonUrl ? errors.buttonUrl : ''
            }
          />

          <EditInput
            title="Secondary Button Text"
            value={values.secondaryButtonText}
            onChange={handleChange}
            type="text"
            placeholder="Optional secondary button text"
            onEditSave={() => {
              setEditIndex(undefined)
            }}
            editing={editIndex === 5}
            onEditClick={() => {
              setEditIndex(5)
            }}
            onCancel={() => {
              setFieldValue(
                'secondaryButtonText',
                mode === 'create' ? '' : content?.secondaryButtonText,
              )
              setEditIndex(undefined)
            }}
            loading={false}
            inputName="secondaryButtonText"
            mode="edit"
          />

          <EditInput
            title="Secondary Button URL"
            value={values.secondaryButtonUrl}
            onChange={handleChange}
            type="text"
            placeholder="Optional secondary button URL"
            onEditSave={() => {
              setEditIndex(undefined)
            }}
            editing={editIndex === 6}
            onEditClick={() => {
              setEditIndex(6)
            }}
            onCancel={() => {
              setFieldValue(
                'secondaryButtonUrl',
                mode === 'create' ? '' : content?.secondaryButtonUrl,
              )
              setEditIndex(undefined)
            }}
            loading={false}
            inputName="secondaryButtonUrl"
            mode="edit"
          />

          <Checkbox
            checkStatus={values.isBackgroundDark}
            handleChange={(status) => setFieldValue('isBackgroundDark', status)}
            key={`isBackgroundDark-checkbox`}
            text="Is Background Dark"
          />

          <Button
            label={proceedText}
            type="submit"
            className="mt-2 w-full"
            disabled={loading}
            loading={loading}
            primary
          />
        </div>
      </form>

      {uploadedImage || uploadedMobileBanner ? (
        <ImageUploadPreviewModal
          successToast={false}
          image={uploadedImage || (uploadedMobileBanner as File)}
          uploadMode="banner"
          cropAspectRatio={uploadedImage ? 16 / 7 : 4 / 5}
          cropLabel={
            uploadedImage
              ? 'Desktop banner crop (16:7)'
              : 'Mobile banner crop (4:5)'
          }
          open={modal === 'imageUploadPreviewModal' && modalOpen}
          setOpen={setModalOpen}
          onCloseCallback={() => {
            uploadedImage
              ? setUploadedImage(undefined)
              : setUploadedMobileBanner(undefined)
          }}
          onSuccessCallback={(url) => {
            if (uploadedImage) {
              setFieldValue('desktopImage', url)
            } else {
              setFieldValue('mobileImage', url)
            }
          }}
        />
      ) : null}
    </div>
  )
}

export const AddHomepageBannerModal: React.FC<{
  open: boolean
  setOpen: (cal: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: (val: Omit<HomepageBannerContent, '_id'>) => void
  mode: 'edit' | 'create'
  content?: HomepageBannerContent
  loading?: boolean
}> = ({
  open,
  setOpen,
  onCloseCallback = () => {},
  onSuccessCallback = () => {},
  mode,
  content,
  loading,
}) => {
  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        onCloseCallback()
      }}
      title={mode === 'create' ? 'Add Explore' : 'Edit Explore'}
      childrenClassName="md:!max-w-[420px]"
    >
      <AddHomeBannerForm
        onSuccessCallback={onSuccessCallback}
        content={content}
        mode={mode}
        open={open}
        loading={loading}
      />
    </ModalComponent>
  )
}
